import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';

export const DEFAULT_SCHOOL = {
  SCHOOL_NAME: 'Vidya Vatika World School',
  SCHOOL_TAGLINE: 'Premier CBSE Institution in Panipat',
  SCHOOL_HERO_SUBLINE: 'Nurturing Excellence, Values & Global Readiness',
  SCHOOL_LOGO_URL: '/images/logo.png',
  SCHOOL_PHONE: '9034851777',
  SCHOOL_PHONE_ALT: '8950105177',
  SCHOOL_LANDLINE: '0180-4017777',
  SCHOOL_EMAIL_INFO: 'info@vidyavatikaworldschool.com',
  SCHOOL_ADDRESS: 'G.T. Road, Near Railway Crossing, Panipat, Haryana 132103',
  ADMISSIONS_SESSION_LABEL: '',
  SHOW_LEADERSHIP_MESSAGE: true,
  LEADERSHIP_MEMBERS: [],
  SHOW_ACHIEVEMENTS_SECTION: true,
  ACHIEVEMENTS_ITEMS: [
    { icon: '🥇', title: 'CBSE Top Ranked', subtitle: '2023-24', isActive: true },
    { icon: '🌟', title: 'Best School Award', subtitle: 'Panipat', isActive: true },
    { icon: '🎯', title: '100% Result', subtitle: 'Class 10 & 12', isActive: true },
    { icon: '🏅', title: 'Sports Excellence', subtitle: 'State Level', isActive: true },
    { icon: '💡', title: 'Innovation Hub', subtitle: 'STEM Education', isActive: true },
    { icon: '✅', title: 'ISO Certified', subtitle: 'Quality Standard', isActive: true },
  ],
  SHOW_HOME_STATS: true,
  HOME_STATS_ITEMS: [
    { key: 'students', number: 1000, suffix: '+', label: 'Students', isActive: true },
    { key: 'teachers', number: 50, suffix: '+', label: 'Teachers', isActive: true },
    { key: 'years', number: 20, suffix: '+', label: 'Years Experience', isActive: true },
    { key: 'result', number: 100, suffix: '%', label: 'Result Focus', isActive: true },
  ],
  DIRECTOR_NAME: 'Krishan Kumar Aggarwal',
  DIRECTOR_MESSAGE:
    'Vidya Vatika World School ka lakshya hai ki har bachche ko behtar shiksha, sanskaar aur suraksha mile. Hum apne school me har student ko ek parivaar ki tarah dekhte hain. Aap sabhi ka school me swagat hai.',
  DIRECTOR_IMAGE_URL: '/images/director.png',
  PRINCIPAL_NAME: 'Suman Sharma',
  PRINCIPAL_MESSAGE:
    "Hamare school me har bachche ki tarakki, unki suraksha aur unka vikas sabse pehle hai. Vidya Vatika World School me aapke bachche ko padhai ke sath-sath sanskaar aur jeevan ke moolya bhi sikhaye jaate hain.",
  PRINCIPAL_IMAGE_URL: '/images/principal.png',
  GOOGLE_MAP_EMBED_URL:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3496.0!2d76.9!3d29.4!2m3!1f0!2f0!3f0!3m3!1m2!1s0x390dda2d86f1666d%3A0xc9ec1efd3f67e55e!2sVidya%20Vatika%20World%20School!5e0!3m2!1sen!2sin!4v1234567890',
};

const DEFAULT_WHATSAPP = {
  WHATSAPP_NUMBER: '919034851777',
  WHATSAPP_ENABLED: true,
};

const SiteSettingsContext = createContext({
  loaded: false,
  school: DEFAULT_SCHOOL,
  whatsapp: DEFAULT_WHATSAPP,
  social: {},
  refresh: async () => {},
});

export function SiteSettingsProvider({ children }) {
  const [loaded, setLoaded] = useState(false);
  const [school, setSchool] = useState(DEFAULT_SCHOOL);
  const [whatsapp, setWhatsapp] = useState(DEFAULT_WHATSAPP);
  const [social, setSocial] = useState({});

  const refresh = async () => {
    const { data } = await apiService.settings.getPublicSite();
    if (data?.school) {
      const next = { ...data.school };
      if (typeof next.LEADERSHIP_MEMBERS === 'string') {
        try {
          const parsed = JSON.parse(next.LEADERSHIP_MEMBERS);
          if (Array.isArray(parsed)) next.LEADERSHIP_MEMBERS = parsed;
        } catch (e) {
          // ignore
        }
      }
      if (typeof next.ACHIEVEMENTS_ITEMS === 'string') {
        try {
          const parsed = JSON.parse(next.ACHIEVEMENTS_ITEMS);
          if (Array.isArray(parsed)) next.ACHIEVEMENTS_ITEMS = parsed;
        } catch (e) {
          // ignore
        }
      }
      if (typeof next.HOME_STATS_ITEMS === 'string') {
        try {
          const parsed = JSON.parse(next.HOME_STATS_ITEMS);
          if (Array.isArray(parsed)) next.HOME_STATS_ITEMS = parsed;
        } catch (e) {
          // ignore
        }
      }
      setSchool((prev) => ({ ...prev, ...next }));
    }
    if (data?.whatsapp) {
      setWhatsapp((prev) => ({ ...prev, ...data.whatsapp }));
    }
    if (data?.social) {
      setSocial(data.social);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refresh();
      } catch (e) {
        /* keep defaults */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();

    // Allow other pages (Admin settings) to request a live refresh.
    const onRefresh = async () => {
      try {
        await refresh();
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('site-settings:refresh', onRefresh);

    return () => {
      cancelled = true;
      window.removeEventListener('site-settings:refresh', onRefresh);
    };
  }, []);

  const value = useMemo(
    () => ({ loaded, school, whatsapp, social, refresh }),
    [loaded, school, whatsapp, social]
  );

  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>;
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}
